import { z } from 'zod'

/**
 * Excel tarzı sütun filtresi modeli. İş alanı bilgisi içermez (AGENTS.md §3): hangi
 * sütunun hangi `FilterFieldKind`e sahip olduğuna çağıran karar verir. Burada yalnızca
 * kind -> izinli operatör eşlemesi ve adres çubuğu (`cf`) serileştirmesi var; Cube
 * filtresine çeviri `services/cube/report.ts`'te.
 */

export type FilterFieldKind = 'text' | 'numeric' | 'bool' | 'date' | 'datetime'

export const CONDITION_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'set',
  'notSet',
] as const

export type ConditionOperator = (typeof CONDITION_OPERATORS)[number]

const COMPARE: ConditionOperator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'set',
  'notSet',
]

export const KIND_OPERATORS: Record<FilterFieldKind, ConditionOperator[]> = {
  text: ['eq', 'neq', 'contains', 'notContains', 'startsWith', 'endsWith', 'set', 'notSet'],
  numeric: COMPARE,
  date: COMPARE,
  datetime: COMPARE,
  bool: [],
}

/** Değer listesi (onay kutuları) sunulan türler; sayı/tarihte yalnızca koşul var. */
export const hasValueList = (kind: FilterFieldKind) => kind === 'text' || kind === 'bool'

/** Değer listesinde en fazla bu kadar değer; fazlası aramayla daraltılır. */
export const VALUE_LIST_LIMIT = 200

export const needsValue = (op: ConditionOperator) => op !== 'set' && op !== 'notSet'

export interface FilterCondition {
  op: ConditionOperator
  value: string
  valueTo?: string
}

/**
 * `values`: onay kutuları. `in` = yalnızca `values` (+ `blank` işaretliyse boşlar);
 * `out` = "hepsi, `values` hariç" (+ `blank` işaretsizse boşlar da hariç) — Excel'de
 * uzun listeden birkaçının işaretini kaldırmak URL'yi şişirmesin.
 */
export type ColumnFilter =
  | { type: 'values'; mode: 'in' | 'out'; values: string[]; blank: boolean }
  | { type: 'conditions'; join: 'and' | 'or'; conditions: FilterCondition[] }

/** Sütun kimliği (nitelikli Cube üyesi) -> filtre. */
export type ColumnFilters = Record<string, ColumnFilter>

const conditionSchema = z.object({
  op: z.enum(CONDITION_OPERATORS),
  value: z.string(),
  valueTo: z.string().optional(),
})

const filterSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('values'),
    mode: z.enum(['in', 'out']),
    values: z.array(z.string()),
    blank: z.boolean(),
  }),
  z.object({
    type: z.literal('conditions'),
    join: z.enum(['and', 'or']),
    conditions: z.array(conditionSchema),
  }),
])

const isCompleteCondition = (condition: FilterCondition) =>
  !needsValue(condition.op) ||
  (condition.value.trim() !== '' &&
    (condition.op !== 'between' || (condition.valueTo ?? '').trim() !== ''))

/** Hiçbir satırı elemeyen (ya da yarım kalmış) filtre `undefined` olur. */
export function normalizeFilter(
  filter: ColumnFilter,
  kind: FilterFieldKind,
): ColumnFilter | undefined {
  if (filter.type === 'values') {
    if (!hasValueList(kind)) return undefined
    const everything = filter.mode === 'out' && filter.values.length === 0 && filter.blank
    return everything ? undefined : filter
  }
  const conditions = filter.conditions
    .filter((condition) => KIND_OPERATORS[kind].includes(condition.op))
    .filter(isCompleteCondition)
  return conditions.length > 0 ? { ...filter, conditions } : undefined
}

/** İstemcide süzülen tablolar için hücre değeri: metin, sayı ya da `YYYY-MM-DD…` takvim günü. */
export type FilterCellValue = string | number | null | undefined

const isBlankCell = (value: FilterCellValue) =>
  value === null || value === undefined || value === ''
const lower = (value: string) => value.toLocaleLowerCase('tr')

function compareOrdered<T extends string | number>(
  value: T,
  op: ConditionOperator,
  from: T,
  to: T,
): boolean {
  switch (op) {
    case 'eq':
      return value === from
    case 'neq':
      return value !== from
    case 'gt':
      return value > from
    case 'gte':
      return value >= from
    case 'lt':
      return value < from
    case 'lte':
      return value <= from
    case 'between':
      return value >= from && value <= to
    default:
      return false
  }
}

function matchesText(value: string, op: ConditionOperator, target: string): boolean {
  switch (op) {
    case 'eq':
      return value === target
    case 'neq':
      return value !== target
    case 'contains':
      return lower(value).includes(lower(target))
    case 'notContains':
      return !lower(value).includes(lower(target))
    case 'startsWith':
      return lower(value).startsWith(lower(target))
    case 'endsWith':
      return lower(value).endsWith(lower(target))
    default:
      return false
  }
}

function matchesCondition(
  value: FilterCellValue,
  kind: FilterFieldKind,
  condition: FilterCondition,
): boolean {
  const { op } = condition
  const blank = isBlankCell(value)
  if (op === 'set') return !blank
  if (op === 'notSet') return blank
  const isDate = kind === 'date' || kind === 'datetime'
  // Cube'ün `notEquals`/`notContains`'ı boşları da döndürür; tarih aralığı dışı döndürmez.
  if (blank) return op === 'notContains' || (op === 'neq' && !isDate)
  if (isDate) {
    // Gün bazında (Excel gibi); takvim günleri metin olarak sıralanabilir.
    const from = condition.value
    return compareOrdered(String(value).slice(0, 10), op, from, condition.valueTo ?? from)
  }
  if (kind === 'numeric') {
    const from = Number(condition.value)
    return compareOrdered(Number(value), op, from, Number(condition.valueTo ?? condition.value))
  }
  return matchesText(String(value), op, condition.value)
}

/**
 * Sütun filtresinin istemci karşılığı; anlamı Cube çevirisiyle aynıdır
 * (`services/cube/report.ts` `columnFilterToCube`). Veri zaten tarayıcıdaysa kullanılır.
 */
export function matchesColumnFilter(
  value: FilterCellValue,
  kind: FilterFieldKind,
  filter: ColumnFilter,
): boolean {
  if (filter.type === 'conditions') {
    const results = filter.conditions.map((condition) => matchesCondition(value, kind, condition))
    return filter.join === 'or' ? results.some(Boolean) : results.every(Boolean)
  }
  if (isBlankCell(value)) return filter.blank
  return filter.values.includes(String(value)) === (filter.mode === 'in')
}

/** `cf` parametresi. Boşsa `undefined` (parametre silinir). */
export function serializeColumnFilters(filters: ColumnFilters): string | undefined {
  return Object.keys(filters).length > 0 ? JSON.stringify(filters) : undefined
}

/** `cf`'yi okur; bilinmeyen sütunlar, geçersiz operatörler ve boş filtreler düşer. */
export function parseColumnFilters(
  raw: string | undefined,
  kinds: ReadonlyMap<string, FilterFieldKind>,
): ColumnFilters {
  if (!raw) return {}
  let payload: unknown
  try {
    payload = JSON.parse(raw)
  } catch {
    return {}
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return {}
  const result: ColumnFilters = {}
  for (const [member, value] of Object.entries(payload)) {
    const kind = kinds.get(member)
    const parsed = filterSchema.safeParse(value)
    if (!kind || !parsed.success) continue
    const filter = normalizeFilter(parsed.data, kind)
    if (filter) result[member] = filter
  }
  return result
}
